import { useMutation, useQuery } from '@tanstack/react-query';
import { usePWAContext } from '../../context/PWAContext';
import {
    deleteVmtSendHistoryEntries,
    getVmtSendHistory,
    normalizeVmtRecordsForSend,
    sendVmtNullSubmission,
    sendVmtRecords,
} from './vmtApi';

export const createVmtHooks = ({ queryClient }) => {
    const useVmtSend = (options = {}) => {
        let offlineQueue;
        let isOffline;
        try {
            const pwa = usePWAContext();
            offlineQueue = pwa?.offlineQueue;
            isOffline = pwa?.isOffline || false;
        } catch {
            offlineQueue = null;
            isOffline = false;
        }

        return useMutation({
            mutationKey: ['vmtSend', options.sendMode || 'general'],
            mutationFn: async (variables) => {
                const payload = {
                    ...variables,
                    records: normalizeVmtRecordsForSend(variables?.records),
                    sendMode: variables?.sendMode || options.sendMode || 'general',
                };
                if (isOffline && offlineQueue) {
                    const queued = offlineQueue.addToQueue({
                        url: payload.sendMode === 'trade' ? '/api/vmt/send-trade' : '/api/vmt/send',
                        method: 'POST',
                        data: payload,
                    });
                    if (queued.queued) {
                        return {
                            queued: true,
                            queueId: queued.queueId,
                            message: 'VMT records queued for offline sync',
                        };
                    }
                }
                return sendVmtRecords(payload);
            },
        });
    };

    const useVmtNullSubmission = () =>
        useMutation({
            mutationFn: sendVmtNullSubmission,
        });

    const useVmtSendHistory = (options = {}) =>
        useQuery({
            queryKey: ['vmtSendHistory', options],
            queryFn: () => getVmtSendHistory(options),
            staleTime: 30 * 1000,
        });

    const useDeleteVmtSendHistory = () =>
        useMutation({
            mutationFn: deleteVmtSendHistoryEntries,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
            },
        });

    return {
        useVmtSend,
        useVmtNullSubmission,
        useVmtSendHistory,
        useDeleteVmtSendHistory,
    };
};
